import { Injectable, Injector } from '@angular/core';
import { TimeRecordingService } from 'src/app/modules/time-recording/services/time-recording.service';
import { NotificationInboxService } from '../notification/inbox/notification-inbox.service';
import { isTerminalSession } from '../session/terminal-session';
import { UpdateService } from './update.service';

/**
 * Aktualisiert die Daten der Mitarbeiter-App, wenn die App wieder in den Vordergrund kommt.
 *
 * `AppComponent` hängt die Ereignisse an (Capacitor `appStateChange`, Push-Tap, Browser
 * `visibilitychange`/`focus`) und meldet sie hierher; entschieden wird hier.
 *
 * Mitarbeiter-Modus: Rückkehr nativ und Push-Tap laden sofort (Anlass 2ec58125a, Feb. 2025 —
 * wer die App öffnet, soll seinen aktuellen Plan sehen), im Browser erst nach 5 Minuten
 * Abwesenheit.
 *
 * Terminal-Modus: nie. Das Terminal gehört keiner Person und zeigt „meine Schichten, meine
 * Abwesenheiten“ nirgends an. Bis zum 14.10.2026 lud es trotzdem bei jeder Rückkehr sechs
 * Endpunkte nach — auf iOS bei jedem Öffnen des NFC-Systemfensters („Immer bereit“, ≤ 60 s),
 * auf Android bei Bildschirm an/aus, Kiosk- und Systemdialogen, im Browser bei Tab- und
 * Fokuswechsel. In Firma 30 waren das rund 6.600 von 11.900 Anfragen am Tag. Die Prüfung liest
 * den Token bei jedem Ereignis neu, ein Wechsel zwischen Terminal und Person greift sofort.
 * `Sync/check` (Herzschlag + Firmeneinstellungen) läuft davon unberührt weiter.
 */
@Injectable({ providedIn: 'root' })
export class ForegroundRefreshService {
  /** Browser: erst nach so langer Abwesenheit neu laden. */
  static readonly BROWSER_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

  private backgroundTimestamp: number | null = null;

  constructor(private readonly injector: Injector) {}

  /** App ging nativ in den Hintergrund oder der Tab wurde verdeckt. */
  markBackground(now = Date.now()): void {
    this.backgroundTimestamp = now;
  }

  /** Nativ (iOS/Android): App ist wieder aktiv. */
  onNativeResume(isLoggedIn: boolean): void {
    this.performAutomaticRefresh('Capacitor App Resume', isLoggedIn);
  }

  /** Nativ: Push-Meldung angetippt. */
  onPushTap(isLoggedIn: boolean): void {
    this.performAutomaticRefresh('Notification tap', isLoggedIn);
  }

  /** Browser: Tab wieder sichtbar. Lädt zusätzlich den eigenen Stempelstatus. */
  onBrowserVisible(isLoggedIn: boolean, now = Date.now()): void {
    this.refreshAfterAbsence('Browser visibilitychange', isLoggedIn, now);
    if (isLoggedIn && !isTerminalSession()) {
      this.injector.get(TimeRecordingService).getEmployeeLastStatus();
    }
  }

  /** Browser: Fenster hat wieder den Fokus. */
  onBrowserFocus(isLoggedIn: boolean, now = Date.now()): void {
    this.refreshAfterAbsence('Browser focus', isLoggedIn, now);
  }

  private refreshAfterAbsence(source: string, isLoggedIn: boolean, now: number): void {
    if (this.backgroundTimestamp === null) return;
    const elapsed = now - this.backgroundTimestamp;
    this.backgroundTimestamp = null;
    if (elapsed >= ForegroundRefreshService.BROWSER_REFRESH_THRESHOLD_MS) {
      this.performAutomaticRefresh(source, isLoggedIn);
    }
  }

  performAutomaticRefresh(source: string, isLoggedIn: boolean): void {
    if (!isLoggedIn || isTerminalSession()) return;

    this.injector.get(NotificationInboxService).refreshCount();
    this.injector.get(UpdateService).performRefresh().subscribe({
      error: (err) =>
        console.error(`Fehler bei der automatischen Aktualisierung (${source}):`, err),
    });
  }
}
